import type { ChipProps } from "@mui/material";

import type {
  BenchmarkDefinition,
  BenchmarksView,
  EvalGridRow,
  QuestionEvalResult,
  RequirementRenderOptions,
} from "./types";

type ScoreColor = "primary" | "success" | "warning" | "error";

export const resultEvidenceHiddenKeys: ReadonlySet<string> = new Set([
  "category",
  "costEstimate",
  "criterionKey",
  "judge",
  "model",
  "observedPreferredTools",
  "observedRecommendedTools",
  "observedRecommendTools",
  "observedTools",
  "passed",
  "preferredTools",
  "processingMs",
  "provider",
  "requestedModel",
  "recommendedTools",
  "recommendTools",
  "tokenUsage",
]);
export const pagePaddingSx = { p: { xs: 2, md: 3 } };
export const panelSx = { p: 3 };
export const compactPanelSx = { p: 2, height: "100%" };
export const evalDialogContentSx = { px: { xs: 3, sm: 4 }, pt: 2, pb: 4 };
export const evalDialogStackSx = { pb: 3 };

export function buildBenchmarksUrl({
  view,
  evalResultId,
  benchmarkKey,
}: {
  view: BenchmarksView;
  evalResultId?: string;
  benchmarkKey?: string;
}) {
  const params = new URLSearchParams();
  params.set("tab", "benchmarks");
  params.set("benchmarksView", view);

  if (evalResultId) {
    params.set("evalResult", evalResultId);
  }

  if (benchmarkKey) {
    params.set("benchmark", benchmarkKey);
  }

  return `/staff/observe?${params.toString()}`;
}

export function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "n/a";
  }
  return `${value.toFixed(2)}%`;
}

export function formatPoints(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}

export function formatDate(value: string | null | undefined) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  }).format(date);
}

export function titleFromKey(value: string) {
  return value
    .split("-")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatEnumLabel(value: string) {
  if (value === "JUDGE_BINARY") {
    return "LLM Judge";
  }
  return titleFromKey(value.toLowerCase().replaceAll("_", "-"));
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isPrimitiveRequirementValue(value: unknown) {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  );
}

export function formatRequirementKey(value: string) {
  const knownLabels: Record<string, string> = {
    anyToolFromEachGroup: "Any tool from each group",
    categorySeparation: "Category separation",
    conditionClassification: "Condition classification",
    currentToolEvidence: "Current tool evidence",
    expectedConditionalStates: "Expected conditional states",
    expectedJurisdictions: "Expected jurisdictions",
    expectedStates: "Expected states",
    expectedUnconditionalStates: "Expected unconditional states",
    finalAnswer: "Final answer",
    forbidExtraStates: "Forbid extra states",
    forbidUnnegatedPhrases: "Forbidden unnegated phrases",
    forbidWebFirst: "Forbid web-first evidence",
    includeAnyFromEachGroup: "Include at least one item from each group",
    failedChecks: "Failed checks",
    firstSuccessfulTool: "First successful tool",
    jurisdictionSet: "Jurisdiction set",
    missingAnyToolFromEachGroup: "Missing any tool from each group",
    missingResources: "Missing resources",
    missingTools: "Missing tools",
    nodeId: "Node ID",
    observedResources: "Observed resources",
    observedTools: "Observed tools",
    requiredTermGroups: "Required term groups",
    requiredTools: "Required tools",
    resources: "Resources",
    scoreAggregationRole: "Score aggregation role",
    sourceId: "Source ID",
    titleIncludes: "Title includes",
    tools: "Tools",
    urlIncludes: "URL includes",
  };
  const readableKey = value.replaceAll("_", "-").replace(/([a-z0-9])([A-Z])/g, "$1-$2");
  return knownLabels[value] ?? titleFromKey(readableKey);
}

export function getRequirementKeyDescription(value: string): string | null {
  const descriptions: Record<string, string> = {
    anyToolFromEachGroup:
      "Every outer group must be satisfied. Within each group, any one listed tool can satisfy that group.",
    categorySeparation:
      "The LLM judge checks that the answer keeps required document or rule categories separate instead of merging them together.",
    conditionClassification:
      "The LLM judge checks which states or jurisdictions are labeled conditional versus unconditional.",
    currentToolEvidence:
      "The deterministic scorer checks successful Scout tool calls from the run. It is about how Scout gathered evidence, not final-answer text.",
    expectedConditionalStates:
      "States or jurisdictions that should be described as conditional in the final answer.",
    expectedJurisdictions: "Jurisdictions that the final answer is expected to include.",
    expectedStates: "States that the final answer is expected to include.",
    expectedUnconditionalStates:
      "States or jurisdictions that should not be described as conditional in the final answer.",
    failedChecks: "Deterministic checks that directly caused this criterion to fail.",
    finalAnswer: "Requirements that the LLM judge applies to Scout's final answer text.",
    firstSuccessfulTool:
      "The first successful evidence tool Scout used in this run. This matters when forbidWebFirst is enabled.",
    forbidExtraStates:
      "When enabled, the judge should fail answers that include extra state-level entries outside the expected set.",
    forbidUnnegatedPhrases:
      "Phrases that should not appear as plain claims in the final answer. Negated or corrected mentions may still be acceptable.",
    forbidWebFirst:
      "Fails when Scout's first successful evidence tool is web_search. This is used when the eval should prove Scout leaned on Encamp's custom tools before open-web search.",
    includeAnyFromEachGroup:
      "Each inner list is alternate wording for one required fact. The answer must include at least one item from every group.",
    jurisdictionSet:
      "The LLM judge compares the answer's jurisdictions or states against the expected set.",
    missingAnyToolFromEachGroup:
      "Tool groups where Scout did not use any acceptable successful tool from that group.",
    missingResources:
      "Configured source URL or document requirements that the deterministic scorer did not find.",
    missingTools: "Required successful tools that were not found in the Scout run.",
    observedResources:
      "Persisted source rows the deterministic scorer considered for resource matching.",
    requiredTermGroups:
      "Required terms or categories that must be represented separately in the final answer.",
    requiredTools: "Required successful evidence tools for the current-tool evidence check.",
    resources:
      "Source URL or document requirements. Pass/fail depends on whether any configured resources are missing.",
    scoreAggregationRole:
      "Mandatory criteria force the question score to zero when they fail. Weighted criteria contribute their own points.",
    sourceId: "Exact source identifier required for a resource match.",
    titleIncludes: "Case-insensitive text that must appear in a matched source title.",
    tools: "Tools that must appear as successful Scout tool calls.",
    urlIncludes: "Case-insensitive text that must appear in a matched source URL.",
  };

  return descriptions[value] ?? null;
}

export function formatRequirementScalar(value: unknown) {
  if (value === null) {
    return "None";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return formatPoints(value);
  }
  if (typeof value === "string") {
    return value;
  }
  return "";
}

export function getRequirementChipLabel(value: unknown): string | null {
  if (isPrimitiveRequirementValue(value)) {
    return formatRequirementScalar(value);
  }
  if (isRecord(value) && typeof value.name === "string") {
    return value.name;
  }
  return null;
}

function shouldHideRequirementKey(key: string, options?: RequirementRenderOptions) {
  return (
    options?.hiddenKeys?.has(key) === true ||
    (options?.hideRequirements === true && key === "requirements")
  );
}

export function hasDisplayValue(value: unknown, options?: RequirementRenderOptions): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.some((item) => hasDisplayValue(item, options));
  }
  if (isRecord(value)) {
    return Object.entries(value).some(([key, nestedValue]) => {
      return !shouldHideRequirementKey(key, options) && hasDisplayValue(nestedValue, options);
    });
  }
  return true;
}

export function formatStatusLabel(status: string) {
  return titleFromKey(status.toLowerCase().replaceAll("_", "-"));
}

export function getBenchmarkDefinition(benchmarks: BenchmarkDefinition[], benchmarkKey: string) {
  return benchmarks.find((benchmark) => benchmark.key === benchmarkKey) ?? null;
}

export function getQuestionDefinition(benchmark: BenchmarkDefinition, questionKey: string) {
  return benchmark.questions.find((question) => question.questionKey === questionKey) ?? null;
}

export function formatQuestionName(questionKey: string) {
  return titleFromKey(questionKey);
}

export function getQuestionPoints(questionResult: QuestionEvalResult) {
  return questionResult.rubricResults.reduce((total, criterion) => {
    return total + criterion.pointsAwarded;
  }, 0);
}

export function getQuestionMaxPoints(questionResult: QuestionEvalResult) {
  return questionResult.rubricResults.reduce((total, criterion) => {
    return total + criterion.maxPoints;
  }, 0);
}

export function getWeightedScore(evalRows: EvalGridRow[]) {
  let weightedScoreTotal = 0;
  let weightTotal = 0;

  for (const evalRow of evalRows) {
    for (const questionResult of evalRow.questionResults) {
      if (questionResult.score === null || questionResult.score === undefined) {
        continue;
      }
      weightedScoreTotal += questionResult.score * questionResult.questionWeight;
      weightTotal += questionResult.questionWeight;
    }
  }

  if (weightTotal > 0) {
    return weightedScoreTotal / weightTotal;
  }

  const scoredRows = evalRows.filter((row) => row.score !== null && row.score !== undefined);
  if (scoredRows.length === 0) {
    return null;
  }

  return scoredRows.reduce((total, row) => total + (row.score ?? 0), 0) / scoredRows.length;
}

export function getLatestEvalRows(evalRows: EvalGridRow[]) {
  const latestEval = evalRows[0];
  if (!latestEval) {
    return [];
  }

  if (latestEval.jobId) {
    return evalRows.filter((row) => row.jobId === latestEval.jobId);
  }

  return [latestEval];
}

export function getCategoryChipColor(category: string): ChipProps["color"] {
  return category ? "info" : "default";
}

export function getAggregationRoleChipColor(role: string): ChipProps["color"] {
  if (role === "mandatory") {
    return "warning";
  }
  if (role === "weighted") {
    return "info";
  }
  return "default";
}

export function shouldShowAggregationRole(role: string | null | undefined) {
  return role === "mandatory";
}

export function getProgressValue(score: number | null | undefined) {
  if (score === null || score === undefined) {
    return 0;
  }
  return Math.min(Math.max(score, 0), 100);
}

export function getScoreColor(score: number | null | undefined): ScoreColor {
  if (score === null || score === undefined) {
    return "primary";
  }
  if (score > 75) {
    return "success";
  }
  if (score > 50) {
    return "warning";
  }
  return "error";
}

export const getScoreProgressColor = getScoreColor;
